import { marked } from "marked";
import DOMPurify from "dompurify";

marked.setOptions({
  gfm: true,
  breaks: false,
});

const HTML_FENCE_RE = /^```(?:html|HTML)?\s*\n?([\s\S]*?)\n?```$/;

/**
 * Defensive fence-strip — belt to the server-side suspenders in
 * apps/api/src/pipeline/skills/html-output.ts's extractArticleHtml().
 * Pipeline content is HTML, not Markdown, by design (ADR-007); if any
 * skill's raw output still carries a stray outer ```html/``` fence (piece
 * 525's bug: one skill didn't run its output through extractArticleHtml),
 * `marked` would otherwise treat the whole body as one fenced code block
 * and render every tag as literal text instead of formatted HTML. Only
 * strips a fence that wraps the ENTIRE trimmed content (anchored), so a
 * genuine inline code fence inside real prose is left alone.
 */
export function stripHtmlFence(content: string | null | undefined): string {
  if (!content) return "";
  const match = content.trim().match(HTML_FENCE_RE);
  return match ? match[1].trim() : content;
}

export function markdownToHtml(md: string | null | undefined): string {
  if (!md) return "";
  try {
    // marked does not strip raw HTML, so its output can carry <script>, inline
    // event handlers, or javascript: URLs embedded in the source markdown
    // (e.g. content extracted from an uploaded library document). Every consumer
    // feeds this string to dangerouslySetInnerHTML — sanitize before returning.
    const raw = marked.parse(stripHtmlFence(md), { async: false }) as string;
    return DOMPurify.sanitize(raw);
  } catch (e) {
    console.error("markdown parse failed", e);
    return `<pre>${escapeHtml(md)}</pre>`;
  }
}

/**
 * Strip the first H1 from a Markdown document. The pipeline writes drafts
 * with the piece title as the leading `# Title` line, but our piece
 * shell already renders the title above the body — we'd be showing it
 * twice without this.
 *
 * Removes:
 *   - leading whitespace + a single `# Heading` line + trailing newline(s)
 *   - any front-matter-style `---\n...\n---` block before the H1
 */
export function stripLeadingH1(md: string | null | undefined): string {
  if (!md) return "";
  let s = md.replace(/^\s*---\n[\s\S]*?\n---\s*\n/, "");
  s = s.replace(/^\s*#\s+[^\n]*\n+/, "");
  return s;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * Strip Markdown to a quick plaintext summary. Used for kanban card excerpts
 * when a draft body is the source. Cheap and approximate.
 */
export function markdownToText(md: string | null | undefined, max = 200): string {
  if (!md) return "";
  const stripped = md
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/`[^`]*`/g, " ")
    .replace(/!\[[^\]]*\]\([^)]*\)/g, " ")
    .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/^[\s>#*_-]+/gm, "")
    .replace(/\n+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return stripped.length > max ? stripped.slice(0, max - 1) + "…" : stripped;
}
